import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceDownstreamWorkbooksFilteredByFieldQuery,
  DatasourceDownstreamWorkbooksFilteredByFieldQueryVariables
} from '../../../gen-src/queries/DatasourceDownstreamWorkbooksFilteredByFieldQuery';
import {
  DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQuery,
  DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQuery';
import {
  DatasourceDownstreamWorkbooksQuery,
  DatasourceDownstreamWorkbooksQuery_publishedDatasources_downstreamWorkbooksConnection_nodes,
  DatasourceDownstreamWorkbooksQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamWorkbooksQuery';
import {
  DatasourceDownstreamWorkbooksWithoutPersonalSpaceQuery,
  DatasourceDownstreamWorkbooksWithoutPersonalSpaceQuery_publishedDatasources_downstreamWorkbooksConnection_nodes,
  DatasourceDownstreamWorkbooksWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamWorkbooksWithoutPersonalSpaceQuery';
import { WorkbookSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceDownstreamWorkbooksFilteredByFieldQueryGql } from '../../queries/DatasourceDownstreamWorkbooksFilteredByFieldQueryGql';
import { DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQueryGql';
import { DatasourceDownstreamWorkbooksQueryGql } from '../../queries/DatasourceDownstreamWorkbooksQueryGql';
import { DatasourceDownstreamWorkbooksWithoutPersonalSpaceQueryGql } from '../../queries/DatasourceDownstreamWorkbooksWithoutPersonalSpaceQueryGql';
import {
  getRowDataForWorkbook,
  getRowDataForWorkbookWithoutPersonalSpace,
  WorkbooksDisplayGrid,
  WorkbooksDisplayGridRowData
} from '../sharedDisplayGrid/WorkbooksDisplayGrid';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

type QueryVariables = DatasourceDownstreamWorkbooksQueryVariables;
type QueryResults = DatasourceDownstreamWorkbooksQuery;
type QueryResultListNode = DatasourceDownstreamWorkbooksQuery_publishedDatasources_downstreamWorkbooksConnection_nodes;
type GridRowData = WorkbooksDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatasourceDownstreamWorkbooksWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatasourceDownstreamWorkbooksWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace =
  DatasourceDownstreamWorkbooksWithoutPersonalSpaceQuery_publishedDatasources_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilter = DatasourceDownstreamWorkbooksFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DatasourceDownstreamWorkbooksFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamWorkbooksConnection;
}

export type DatasourceDownstreamWorkbooksQueryGridProps =
  GridSortProps<WorkbooksValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceDownstreamWorkbooksQueryGrid(props: DatasourceDownstreamWorkbooksQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamWorkbooksLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredWorkbooksLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with selection
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DatasourceDownstreamWorkbooksFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        // Query without selection
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={DatasourceDownstreamWorkbooksQueryGql}
          partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as WorkbookSortOrder }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForWorkbook}
        >
          {({ rowStore }) => <WorkbooksDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatasourceDownstreamWorkbooksFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={DatasourceDownstreamWorkbooksWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as WorkbookSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
        >
          {({ rowStore }) => <WorkbooksDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
